import { getKpiBaseUrlForBox, getKpiBaseWithSharedPartUrlForBox } from "../../kpi/module/kpi-module-routing.module";
import { BoxItem } from "../service/box-file-structure.service";

export const BOX_API_URL = 'https://api.box.com/2.0/';
export const LIMIT_NUMBER_SIMULTANEOUS_UPLOADS_IN_BOX = 200;
export const MOBILE_SCREEN_SIZE = 479;
export const DATA_ROOM_FOLDER_NAME = 'Data room';
export const PORTFOLIO_FOLDER_NAME = 'Portfolio';

export class BoxUrls {
  private readonly boxUrlPart = 'box/';
  private readonly dataRoomUrlPart = 'data-room/';
  private readonly userUrlPart = 'user/';
  private readonly authUrlPart = 'auth/';
  private readonly kpiPart = getKpiBaseUrlForBox();
  private readonly kpiSharedPart = getKpiBaseWithSharedPartUrlForBox();

  readonly fmsBox: string;
  readonly adminDataRoom: string;
  readonly portalFms: string;
  readonly portalDataRoom: string;
  readonly externalUserDataRoom: string;
  readonly externalUserDataRoomAuth: string;
  readonly portalUserDataRoom: string;
  readonly kpiBox: string;
  readonly kpiSharedBox: string;

  constructor(fmsUrl: string, portalUrl: string, externalDataRoomUrl: string) {
    this.fmsBox = fmsUrl + this.boxUrlPart;
    this.portalFms = portalUrl;
    this.adminDataRoom = this.fmsBox + this.dataRoomUrlPart;
    this.portalDataRoom = this.portalFms + this.dataRoomUrlPart;
    this.externalUserDataRoom = externalDataRoomUrl;
    this.externalUserDataRoomAuth = this.externalUserDataRoom + this.authUrlPart;
    this.portalUserDataRoom = this.portalDataRoom + this.userUrlPart;
    this.kpiBox = this.fmsBox + this.kpiPart;
    this.kpiSharedBox = this.fmsBox + this.kpiSharedPart;
  }
}

export class BoxShowFlags {
  renameModal = false;
  moveModal = false;
  boxDiv = true;
  docsChat = false;
  uploadState = false;
  showModalThinkTwice = false;
}

export interface BoxItemAction {
  name: string;
  iconAndStyling: string;
  type: BoxItemActionType;
  nameStyling?: string;
}

export enum BoxItemActionType {
  SELECT_ALL = 'SELECT_ALL',
  CLEAR_ALL = 'CLEAR_ALL',
  RENAME = 'RENAME',
  DUPLICATE = 'DUPLICATE',
  MOVE = 'MOVE',
  DELETE = 'DELETE',
  DOWNLOAD = 'DOWNLOAD',
  UPLOAD_FILE = 'UPLOAD_FILE',
  CREATE_FOLDER = 'CREATE_FOLDER',
  PREVIEW = 'PREVIEW',
  FINAL_SELECT_IN_MODAL = 'FINAL_SELECT_IN_MODAL',
}

export const SELECT_ALL_ACTION: BoxItemAction = {
  name: 'Select all',
  type: BoxItemActionType.SELECT_ALL,
  iconAndStyling: '',
};

export const CLEAR_ALL_ACTION: BoxItemAction = {
  name: 'Clear all',
  type: BoxItemActionType.CLEAR_ALL,
  iconAndStyling: '',
};

export const RENAME_ACTION: BoxItemAction = {
  name: 'Rename',
  iconAndStyling: 'edit.svg',
  type: BoxItemActionType.RENAME,
  nameStyling: 'disabled',
};

export const DUPLICATE_ACTION: BoxItemAction = {
  name: 'Duplicate',
  iconAndStyling: 'copy.svg',
  type: BoxItemActionType.DUPLICATE,
};

export const MOVE_ACTION: BoxItemAction = {
  name: 'Move',
  iconAndStyling: 'diagonal-arrow.svg',
  type: BoxItemActionType.MOVE,
};

export const DOWNLOAD_ACTION: BoxItemAction = {
  name: 'Download',
  iconAndStyling: 'excel.svg',
  type: BoxItemActionType.DOWNLOAD,
};

export const UPLOAD_ACTION: BoxItemAction = {
  name: 'Upload',
  iconAndStyling: 'upload-arrow-in-rectangle.svg',
  type: BoxItemActionType.UPLOAD_FILE,
};

export const CREATE_FOLDER_ACTION: BoxItemAction = {
  name: 'Folder',
  iconAndStyling: 'plus.svg',
  type: BoxItemActionType.CREATE_FOLDER,
};

export const DELETE_ACTION: BoxItemAction = {
  name: 'Delete',
  iconAndStyling: 'delete.svg; red-11',
  type: BoxItemActionType.DELETE,
  nameStyling: 'red-11',
};

export interface SelectedModalFile {
  id: string;
  name: string;
}

export interface ModalSelectorMode {
  enabled: boolean;
  isOnlyAiSupportedFilesCanBeSelected: boolean;
}

/**
 * Info which is used when user tries to delete some box items.
 * It displays in "thinktwice" modal.
 */
export class DeleteBoxItemsInfo {
  /**
   * Items from file picker which user selected to delete
   */
  itemsToDelete: BoxItem[];
  /**
   * Header of "thinktwice" modal window. May be:
   * 1. Delete folder
   * 2. Delete file
   * 3. Delete X items
   */
  modalHeader: string;
  /**
   * Main text in "thinktwice" modal. May be:
   * 1. Folder _foldeName_ will be deleted
   * 2. File _fileName_ will be deleted
   * 3. X items will be deleted
   */
  modalMainText: string;

  constructor(items: BoxItem[]) {
    this.itemsToDelete = items;
    if (items.length === 1) {
      const itemType = items[0].type.toString();
      this.modalHeader = 'Delete ' + itemType;
      this.modalMainText = itemType[0].toUpperCase() + itemType.slice(1) + ' ' + items[0].name + ' will be deleted';
    } else {
      this.modalHeader = 'Delete ' + items.length + ' items';
      this.modalMainText = items.length + ' items will be deleted';
    }
  }
}

/**
 * Downloads box files and folders by creating <a> html element and clicking on it :)
 * After click <a> element will be deleted
 *
 * @param url url that will be in the href attribute of <a> element
 */
export function downloadBoxItems(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Updates the display state of items in the dropdown action list based on the current selection.
 * This is triggered when the user hovers over the action button and after select/clear all options click.
 *
 * For example, the "Rename" option should only be enabled when exactly one item is selected.
 * Actions like "Delete", "Move", "Duplicate", and "Download" require at least one selected item
 * to be enabled.
 *
 * @param actions - The list of actions to update based on the selection.
 * @param selectedCount - The number of currently selected items.
 */
export function refreshActions(actions: BoxItemAction[], selectedCount: number): void {
  const disabledClass = 'disabled';

  const alwaysNeedsSelection = new Set([
    BoxItemActionType.DUPLICATE,
    BoxItemActionType.MOVE,
    BoxItemActionType.DELETE,
    BoxItemActionType.DOWNLOAD,
  ]);

  actions.forEach((action) => {
    /**
     * Rename action must be enabled only when user selected ONE item from picker
     */
    if (action.type === BoxItemActionType.RENAME) {
      const isDisabled = selectedCount !== 1;
      action.nameStyling = isDisabled ? disabledClass : '';
    } else if (alwaysNeedsSelection.has(action.type)) {
      /**
       * Delete, move, duplicate actions must be disabled if nothing was selected
       */
      action.nameStyling = selectedCount ? '' : disabledClass;
    }
  });
}

/**
 * Compares the selected and previewed collections of box files and determines whether they differ.
 * Returns `true` if the file items in the two collections are different, and `false` if they are the same.
 * Folder items are ignored in the comparison.
 *
 * Why:
 * This function is used to determine whether the preview button should be enabled.
 * Since the preview container cannot display folders, we only consider differences in file items.
 * If only folders differ, the function returns `false`, meaning the preview remains unchanged.
 *
 * @param selected - Items currently selected by the user.
 * @param previewed - Items currently shown in the preview.
 * @returns `true` if file items differ between the two collections, otherwise `false`.
 */
export function isSelectedDistinctFromPreviewed(selected: BoxItem[], previewed: BoxItem[]): boolean {
  const selectedIds = selected
    .filter((item) => item.type !== 'folder')
    .map((item) => item.id);
  const previewedIds = previewed
    .filter((item) => item.type !== 'folder')
    .map((item) => item.id);
  const diffFiles = [
    ...selectedIds.filter((item) => !previewedIds.includes(item)),
    ...previewedIds.filter((item) => !selectedIds.includes(item)),
  ];
  return !diffFiles.length;
}
